import { css } from '@emotion/react';
import { Link,withPrefix } from 'gatsby';
import { useState, useEffect, useMemo } from 'react';
import { ButtonLink } from '@/components/shared/ButtonLink';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { pagesPath } from '@/lib/pagesPath';
import { getScreen } from '@/styles/media-query';
import { AnchorLink } from 'gatsby-plugin-anchor-links';

export type MenuProps = {
  className?: string;
  pathname: string;
};

export const Menu: React.FC<MenuProps> = ({ className, pathname }) => {
  const [isShownMenu, setIsShownMenu] = useState(false);
  const { lock, unlock } = useBodyScrollLock();
  const isTop = useMemo(() => pathname === '/', [pathname]);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(getScreen('pc-menu'));
    const cb = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsShownMenu(false);
        unlock();
      }
    };

    mediaQueryList.addEventListener('change', cb);
    
    setIsMounted(true);
    return () => {
      mediaQueryList.removeEventListener('change', cb);
    };
  }, []);

  return (
    <>
    {isMounted && (
    <div
      css={css`
        position: fixed;
        z-index: 1000;
      `}
      className={className}
    >
      <nav
        css={css`
          position: fixed;
          top: 0;
          right: 0;
          overflow: hidden;
          width: 100%;
          height: 100%;

          ${!isShownMenu &&
          css`
            pointer-events: none;
          `}
        `}
        onClick={() => {
          setIsShownMenu(false);
          unlock();
        }}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            overflow-y: auto;
            background: #fff;
            transform: translateX(100%);
            display: block;
            pointer-events: all;
            width: 80%;
            height: 100%;
            overflow-y: auto;
            margin-left: auto;
            padding: 32px 40px 32px 32px;
            transition: transform 0.2s ease-out;
            overflow-y: auto;

            ${isShownMenu &&
            css`
              transform: translateX(0);
            `}
          `}
        >
          <p
            css={css`
              border-bottom: 1px solid #d1d8dd;
              padding-bottom: 32px;
              margin-bottom: 32px;
            `}
          >
            {isTop ? (
              <span
                css={css`
                  display: block;
                  width: 146px;
                  margin-left: -8px;
                `}
              >
                <img
                  css={css`
                    width: 100%;
                    height: auto;
                  `}
                  src={withPrefix('/images/logo.svg')}
                  alt="Uniforce(ユニフォース) IPO準備クラウド"
                  width="174"
                  height="46"
                />
              </span>
            ) : (
              <Link
                css={css`
                  display: block;
                  width: 146px;
                  margin-left: -8px;
                `}
                to={pagesPath.$url()}
              >
                <img
                  css={css`
                    width: 100%;
                    height: auto;
                  `}
                  src={withPrefix('/images/logo.svg')}
                  alt="Uniforce(ユニフォース) IPO準備クラウド"
                  width="174"
                  height="46"
                />
              </Link>
            )}
          </p>
          <ul
            css={css`
              display: flex;
              list-style: none;
              padding-left: 2px;
              flex-direction: column;
              gap: 36px;
            `}
          >
            {[
              {
                title: 'Uniforceの特徴',
                to: pagesPath.features.$url(),
              },
              {
                title: '主な機能',
                to: pagesPath.tool.$url(),
              },
              {
                title: '料金プラン',
                to: pagesPath.plan.$url(),
              },
              {
                title: 'セキュリティ',
                to: pagesPath.security.$url(),
              },
              {
                title: 'お知らせ',
                to: pagesPath.news.$url(),
              },
              {
                title: <><img src={withPrefix('/images/menu_phaseinfo.png')} alt="" width="100" /></>,
                to: '/#phaseinfo',
              },
              {
                title: 'よくあるご質問',
                to: pagesPath.faq.$url(),
              },
              {
                title: 'お問い合わせ',
                to: pagesPath.contact.$url(),
              },
            ].map((item) => {
              if(item.to.match(/#/) && !isTop){
                return (
                <li key={item.title}>
                  <AnchorLink
                    to={item.to}
                    onClick={() => {
                      setIsShownMenu(false);
                      unlock(true);
                    }}
                  >
                    {item.title}
                  </AnchorLink>
                </li>
                )
              }else{
                return (
                <li key={item.title}>
                  <Link
                    css={css`
                      text-align: left;
                      font: normal normal bold 14px/20px 'Noto Sans JP',
                        sans-serif;
                      letter-spacing: 0.56px;
                      color: #282b40;
                    `}
                    to={item.to}
                    onClick={() => {
                      setIsShownMenu(false);
                      unlock(true);
                    }}
                  >
                    {item.title}
                  </Link>
                </li>
                )
              }
            })}
          </ul>
          <ul
            css={css`
              align-items: center;
              display: flex;
              list-style: none;
              gap: 30px;
              padding-left: 2px;
              flex-direction: column;
              margin-top: 48px;
              align-items: stretch;
            `}
          >
            {[
              {
                title: '資料ダウンロード',
                to: pagesPath.download.$url(),
                variant: 'header-download' as const,
                linkCss: css`
                  width: 100% !important;
                `,
              },
            ].map((item) => (
              <li key={item.title}>
                <ButtonLink
                  css={item.linkCss}
                  to={item.to}
                  variant={item.variant}
                >
                  {item.title}
                </ButtonLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>
      <button
        css={css`
          position: fixed;
          right: 20px;
          bottom: 20px;
          cursor: pointer;
          display: block;
          border: none;
          font-family: inherit;
          background: linear-gradient(180deg, #00b3a7 0%, #00c9be 100%);
          border-radius: 9999px;
          box-shadow: 0px 0px 8px #5d5d5d54;
          width: 64px;
          height: 64px;

          ${isShownMenu &&
          css`
            background: #def6f5;
          `}
        `}
        onClick={() => {
          if (isShownMenu) {
            setIsShownMenu(false);
            unlock();
          } else {
            setIsShownMenu(true);
            lock();
          }
        }}
      >
        <img
          src={!isShownMenu ? withPrefix('/images/menu.svg') : withPrefix('/images/menu-close.svg') }
          width="64"
          height="64"
          alt={!isShownMenu ? 'メニューを開く' : 'メニューを閉じる'}
        />
      </button>
    </div>
    )}
    </>
  );
};
